import React, { useContext, useState, useEffect } from "react";

import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import dynamic from "next/dynamic";
import Image from "next/legacy/image";
import { useSearchParams, useParams } from "../../hooks/useNavigation";
import { isMobile } from "react-device-detect";

import ComponentContext from "../../../contexts/ComponentContext";
import { useStores } from "../../../contexts/StoreContext";
import {
  useAnalytics,
  getUTMFromParams,
} from "../../helpers/AnalyticsTrackingContext";
import { manageSessionId } from "../../helpers/tracking";
import { contractTypeMap, hoursTypeMap } from "../../helpers/vacancyHelpers";
import Button from "../Button/Button";
import { normalizeVacancyPoint } from "../Map/normalizeMarkerDetails/normalizeMarkerDetails";
import ApplicationFormModal from "../SearchResults/ApplicationFormModal";

const DynamicTFChatBot = dynamic(
  () =>
    import("@e4s-developers/tf-careersite-chatbot").then((mod) => {
      return mod.TFChatBot;
    }),
  {
    ssr: false,
  }
);

function JobSummaryWithMap() {
  const { sendTrackingEvent } = useAnalytics();
  const { vacancyStore, modalStore, configStore, trackingStore } = useStores();
  const { config } = configStore;
  const { openModal } = modalStore;
  const { selectedVacancy } = vacancyStore;
  const params = useParams();
  const searchParams = useSearchParams();
  const search = Object.fromEntries(searchParams.entries());
  const combindedParms = { ...params, ...search };
  const smartApply = combindedParms["smart-apply"];
  const isInteractiveApply = smartApply === "true";
  const { refererQueryString } = trackingStore;
  const utm = getUTMFromParams(refererQueryString);

  const { formHeaderBackgroundColor } = useContext(ComponentContext);
  const formHeaderTextColor =
    config?.applicationFormConfig?.textColor || "#FFFFFF";
  const backgroundColor = config?.applicationFormConfig?.backgroundColor;

  const [showInteractiveApp, setShowInteractiveApp] = useState(false);
  const [isVacancyActive, setIsVacancyActive] = useState(true);

  useEffect(() => {
    if (!isEmpty(selectedVacancy)) {
      const isVacancyClosed = selectedVacancy.status === "CLOSED";
      setIsVacancyActive(!isVacancyClosed);
    }
  }, [selectedVacancy]);

  const { image } = useContext(ComponentContext);

  const imageUrl = image?.url;

  const hoursType = selectedVacancy?.shiftTypes || [];

  const firstLine = [
    selectedVacancy?.companyName,
    selectedVacancy?.location?.city,
  ]
    .filter(Boolean)
    .join(", ");

  const details = [
    firstLine,
    selectedVacancy?.remuneration.description,
    hoursType.join(", "),
    hoursTypeMap[selectedVacancy?.hoursType],
    contractTypeMap[selectedVacancy?.contractType],
  ].filter(Boolean);

  const normalizedVacancyPoint = selectedVacancy
    ? normalizeVacancyPoint(selectedVacancy)
    : null;

  return (
    <div className="row">
      <div className="col-md-6">
        <div className="d-flex flex-column justify-content-between h-100">
          <div className="mb-5">
            <h1 className="fw-bold mb-5 text-uppercase">
              {selectedVacancy.title}
            </h1>
            <div className="mb-5">
              {details.map((detail) => (
                <p key={detail} className="mb-0">
                  {detail}
                </p>
              ))}
            </div>
            {isVacancyActive ? (
              <div className="d-flex gap-2">
                <Button
                  className="px-5"
                  onClick={() => {
                    const jobId =
                      selectedVacancy.vacancyId || selectedVacancy.id;
                    sendTrackingEvent("APPLY_BUTTON_CLICK", jobId, {
                      vacancyId: jobId,
                    });
                    openModal({
                      content: <ApplicationFormModal />,
                      fullscreen: isMobile,
                    });
                  }}
                >
                  Apply now
                </Button>
                {config?.allowInteractiveApplications || isInteractiveApply ? (
                  <Button
                    className="px-5 ms-3"
                    onClick={() => {
                      const jobId =
                        selectedVacancy.vacancyId || selectedVacancy.id;
                      sendTrackingEvent("SMART_APPLY_BUTTON_CLICK", jobId, {
                        vacancyId: jobId,
                      });

                      setShowInteractiveApp(!showInteractiveApp);
                    }}
                  >
                    <span style={{ alignItems: "center", display: "flex" }}>
                      <svg
                        className="icon"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        style={{
                          height: "24px",
                          marginRight: "10px",
                          width: "24px",
                        }}
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 01-.923 1.785A5.969 5.969 0 006 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Smart Apply
                    </span>
                  </Button>
                ) : null}
              </div>
            ) : null}
            {showInteractiveApp && (
              <DynamicTFChatBot
                formId={selectedVacancy?.applicationDetail?.appId}
                sId={manageSessionId()}
                utm={utm}
                onClose={() => setShowInteractiveApp(false)}
                show={showInteractiveApp}
                tenant={selectedVacancy.tenant}
                vacancyTitle={selectedVacancy.title}
                style={{
                  headerBackgroundColour:
                    backgroundColor || formHeaderBackgroundColor,
                  headerTextColour: formHeaderTextColor,
                  headerLogo: selectedVacancy?.company?.logoUrlFull,
                }}
              />
            )}
          </div>
          <div className="mt-auto">
            {imageUrl && (
              <Image
                alt={image?.alternativeText}
                height={image?.height || 50}
                layout="responsive"
                src={imageUrl}
                width={image?.width || 100}
              />
            )}
          </div>
        </div>
      </div>
      <div className="col-md-6">
        {normalizedVacancyPoint && (
          <iframe
            title="map"
            height="450"
            width="100%"
            loading="lazy"
            allowfullscreen
            referrerPolicy="no-referrer-when-downgrade"
            src={`https://www.google.com/maps/embed/v1/place?q=${normalizedVacancyPoint.coords.lat}%2C${normalizedVacancyPoint.coords.lng}&key=${config.googleMapsAPIKey}`}
          />
        )}
      </div>
    </div>
  );
}

export default observer(JobSummaryWithMap);
